import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const ContentMonitoringPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Content Monitoring Policy"
        description="CSW Content Monitoring Policy. Learn how we monitor and moderate content in our virtual study rooms to maintain a safe learning environment."
        keywords="csw content policy, study room moderation, content guidelines"
        canonicalUrl="/content-monitoring-policy/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                Content Monitoring Policy
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              CSW – Chill Study Work App
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 md:p-12">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              
              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At CSW – Chill Study Work App ("CSW," "we," "our," or "us"), we are committed to maintaining a safe, respectful, and secure environment for all users. This Content Monitoring Policy outlines our approach to monitoring user-generated content to ensure it complies with our community standards and legal obligations.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. Purpose of Content Monitoring</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">The primary purpose of content monitoring on CSW is to:</p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Protect users from harmful, inappropriate, or illegal content.</li>
                  <li>Ensure compliance with applicable laws and regulations.</li>
                  <li>Uphold the integrity of our platform and maintain a positive user experience.</li>
                </ul>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. Types of Content Monitored</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">We monitor all forms of content generated and shared on the CSW platform, including but not limited to:</p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Text (messages, comments, posts)</li>
                  <li>Images and videos</li>
                  <li>Links to external websites</li>
                  <li>Usernames and profile information</li>
                  <li>Any other content or media uploaded by users</li>
                </ul>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Content Standards</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">The following types of content are prohibited on CSW:</p>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Hate Speech:</strong> Content that promotes or incites violence, discrimination, or hostility based on race, ethnicity, religion, gender, sexual orientation, disability, or any other characteristic protected by law.</p>
                  <p><strong className="text-foreground">Harassment and Bullying:</strong> Any content that targets individuals or groups with the intent to harass, intimidate, or cause harm.</p>
                  <p><strong className="text-foreground">Violence:</strong> Content that depicts or encourages violence, self-harm, or threats against individuals or groups.</p>
                  <p><strong className="text-foreground">Sexual Content:</strong> Explicit sexual content, nudity, or any other content that is sexually suggestive or inappropriate for a general audience.</p>
                  <p><strong className="text-foreground">Illegal Activities:</strong> Content that promotes or facilitates illegal activities, including but not limited to drug use, trafficking, or criminal behavior.</p>
                  <p><strong className="text-foreground">Politically Sensitive Content:</strong> Content that may incite political unrest, violence, or that involves highly sensitive political matters.</p>
                  <p><strong className="text-foreground">Gambling:</strong> Content that promotes, facilitates, or advertises gambling activities.</p>
                  <p><strong className="text-foreground">Misinformation:</strong> Content that spreads false or misleading information, including deceptive practices such as phishing or scams.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Monitoring Methods</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">To enforce our content standards, we use a combination of automated and manual monitoring methods:</p>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">AI Moderation:</strong> We employ AI-powered tools (e.g., GetStream AI Moderation) to automatically scan and analyze user-generated content in real-time. These tools help identify and flag content that violates our standards.</p>
                  <p><strong className="text-foreground">Keyword Filtering:</strong> Specific keywords and phrases associated with prohibited content are automatically filtered to prevent their use on the platform.</p>
                  <p><strong className="text-foreground">User Reporting:</strong> Users can report content they believe violates our content standards. Reports are reviewed by our moderation team, who take appropriate action as necessary.</p>
                  <p><strong className="text-foreground">Manual Review:</strong> Our moderation team manually reviews flagged content and user reports to ensure accurate enforcement of our content standards.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. Enforcement and Consequences</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">If content is found to violate our standards:</p>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Content Removal:</strong> The violating content will be promptly removed from the platform.</p>
                  <p><strong className="text-foreground">Account Actions:</strong> Depending on the severity of the violation, users may receive warnings, have their accounts temporarily suspended, or face permanent account termination.</p>
                  <p><strong className="text-foreground">Legal Reporting:</strong> In cases involving illegal content or activities, we may report the user and the content to relevant law enforcement authorities.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. User Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  While monitoring content, we are committed to respecting users' privacy. Content monitoring is conducted solely to enforce our community standards and legal obligations. Any data collected through monitoring is handled in accordance with our Privacy Policy.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. User Responsibilities</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By using CSW, users agree to comply with our content standards and are responsible for the content they generate and share on the platform. Users are encouraged to report any content they believe violates these standards.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">9. Policy Updates</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Content Monitoring Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage users to review this policy periodically.
                </p>
              </div>

              <div className="mb-0">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">10. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have any questions or concerns about this Content Monitoring Policy, please contact us at:
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <p className="font-semibold text-foreground mb-2">CSW – Chill Study Work App</p>
                  <a href="mailto:info@csw.live" className="text-primary hover:text-primary/80 transition-colors">
                    info@csw.live
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContentMonitoringPolicy;
